/**
 * Australian personal-finance category taxonomy.
 *
 * This is committed data — a source-of-truth catalogue used by the pipeline's
 * rules engine, the Haiku categoriser, and the viewer's display layer.
 * Changes here are intentional schema evolutions: raise a PR for review.
 *
 * isSpend:
 *   true  — counts towards spending totals (excluded: income, transfers,
 *            savings, loan repayments).
 *   false — does not count towards spending totals.
 */

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export interface CategoryGroup {
  id: string;
  name: string;
}

export const GROUPS: ReadonlyArray<CategoryGroup> = Object.freeze([
  { id: 'income', name: 'Income' },
  { id: 'groceries-food', name: 'Groceries & Food' },
  { id: 'eating-out', name: 'Eating Out & Takeaway' },
  { id: 'transport', name: 'Transport' },
  { id: 'utilities-bills', name: 'Utilities & Bills' },
  { id: 'health', name: 'Health & Medical' },
  { id: 'shopping', name: 'Shopping & Personal' },
  { id: 'housing', name: 'Housing' },
  { id: 'entertainment', name: 'Entertainment & Leisure' },
  { id: 'travel', name: 'Travel' },
  { id: 'education', name: 'Education' },
  { id: 'financial', name: 'Financial & Insurance' },
  { id: 'savings-investments', name: 'Savings & Investments' },
  { id: 'transfers', name: 'Transfers' },
  { id: 'uncategorised', name: 'Uncategorised' },
]);

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export interface CategoryRecord {
  id: string;
  groupId: string;
  name: string;
  isSpend: boolean;
  /** When true, this category is the default for needs-review items. */
  isNeedsReviewDefault?: true;
}

export const CATEGORIES: ReadonlyArray<CategoryRecord> = Object.freeze([
  // Income
  { id: 'salary', groupId: 'income', name: 'Salary / Wages', isSpend: false },
  { id: 'freelance', groupId: 'income', name: 'Freelance / Contract', isSpend: false },
  { id: 'rental-income', groupId: 'income', name: 'Rental Income', isSpend: false },
  { id: 'dividends', groupId: 'income', name: 'Dividends', isSpend: false },
  { id: 'tax-refund', groupId: 'income', name: 'Tax Refund (ATO)', isSpend: false },
  { id: 'government-payments', groupId: 'income', name: 'Government Payments (Centrelink)', isSpend: false },
  { id: 'other-income', groupId: 'income', name: 'Other Income', isSpend: false },

  // Groceries & Food
  { id: 'supermarket', groupId: 'groceries-food', name: 'Supermarket', isSpend: true },
  { id: 'fruit-veg', groupId: 'groceries-food', name: 'Fruit & Veg Market', isSpend: true },
  { id: 'butcher-deli', groupId: 'groceries-food', name: 'Butcher / Deli', isSpend: true },
  { id: 'online-grocery', groupId: 'groceries-food', name: 'Online Grocery Delivery', isSpend: true },

  // Eating Out & Takeaway
  { id: 'restaurant', groupId: 'eating-out', name: 'Restaurant', isSpend: true },
  { id: 'cafe', groupId: 'eating-out', name: 'Café / Coffee', isSpend: true },
  { id: 'fast-food', groupId: 'eating-out', name: 'Fast Food / Takeaway', isSpend: true },
  { id: 'food-delivery', groupId: 'eating-out', name: 'Food Delivery (UberEats / Menulog)', isSpend: true },
  { id: 'bar-pub', groupId: 'eating-out', name: 'Bar / Pub', isSpend: true },

  // Transport
  { id: 'fuel', groupId: 'transport', name: 'Fuel', isSpend: true },
  { id: 'public-transport', groupId: 'transport', name: 'Public Transport (Opal / Myki)', isSpend: true },
  { id: 'rideshare', groupId: 'transport', name: 'Rideshare (Uber / DiDi)', isSpend: true },
  { id: 'parking', groupId: 'transport', name: 'Parking', isSpend: true },
  { id: 'car-registration', groupId: 'transport', name: 'Car Registration', isSpend: true },
  { id: 'car-insurance', groupId: 'transport', name: 'Car Insurance', isSpend: true },
  { id: 'car-maintenance', groupId: 'transport', name: 'Car Maintenance & Repairs', isSpend: true },
  { id: 'tolls', groupId: 'transport', name: 'Tolls (e-TAG)', isSpend: true },

  // Utilities & Bills
  { id: 'electricity', groupId: 'utilities-bills', name: 'Electricity', isSpend: true },
  { id: 'gas', groupId: 'utilities-bills', name: 'Gas', isSpend: true },
  { id: 'water', groupId: 'utilities-bills', name: 'Water / Sewerage', isSpend: true },
  { id: 'internet', groupId: 'utilities-bills', name: 'Internet (NBN / Mobile Broadband)', isSpend: true },
  { id: 'mobile-phone', groupId: 'utilities-bills', name: 'Mobile Phone', isSpend: true },
  { id: 'home-phone', groupId: 'utilities-bills', name: 'Home Phone / VoIP', isSpend: true },
  { id: 'streaming', groupId: 'utilities-bills', name: 'Streaming (Netflix / Stan / Disney+)', isSpend: true },
  { id: 'software-subscriptions', groupId: 'utilities-bills', name: 'Software Subscriptions', isSpend: true },
  { id: 'council-rates', groupId: 'utilities-bills', name: 'Council Rates', isSpend: true },
  { id: 'body-corporate', groupId: 'utilities-bills', name: 'Body Corporate / Strata', isSpend: true },

  // Health & Medical
  { id: 'gp-specialist', groupId: 'health', name: 'GP & Specialist', isSpend: true },
  { id: 'pharmacy', groupId: 'health', name: 'Pharmacy / PBS Prescriptions', isSpend: true },
  { id: 'dental', groupId: 'health', name: 'Dental', isSpend: true },
  { id: 'optical', groupId: 'health', name: 'Optical', isSpend: true },
  { id: 'allied-health', groupId: 'health', name: 'Allied Health (Physio / Chiro / Psych)', isSpend: true },
  { id: 'health-insurance', groupId: 'health', name: 'Private Health Insurance', isSpend: true },
  { id: 'gym-fitness', groupId: 'health', name: 'Gym & Fitness', isSpend: true },

  // Shopping & Personal
  { id: 'clothing', groupId: 'shopping', name: 'Clothing & Accessories', isSpend: true },
  { id: 'electronics', groupId: 'shopping', name: 'Electronics & Technology', isSpend: true },
  { id: 'homewares', groupId: 'shopping', name: 'Homewares & Furniture', isSpend: true },
  { id: 'personal-care', groupId: 'shopping', name: 'Personal Care & Beauty', isSpend: true },
  { id: 'gifts', groupId: 'shopping', name: 'Gifts & Occasions', isSpend: true },
  { id: 'books-stationery', groupId: 'shopping', name: 'Books & Stationery', isSpend: true },
  { id: 'pets', groupId: 'shopping', name: 'Pet Supplies & Vet', isSpend: true },
  { id: 'online-shopping', groupId: 'shopping', name: 'Online Shopping (Amazon / eBay)', isSpend: true },
  { id: 'hardware-garden', groupId: 'shopping', name: 'Hardware & Garden', isSpend: true },

  // Housing
  { id: 'rent', groupId: 'housing', name: 'Rent', isSpend: true },
  { id: 'mortgage-repayment', groupId: 'housing', name: 'Mortgage Repayment', isSpend: false },
  { id: 'home-insurance', groupId: 'housing', name: 'Home & Contents Insurance', isSpend: true },
  { id: 'repairs-maintenance', groupId: 'housing', name: 'Home Repairs & Maintenance', isSpend: true },
  { id: 'cleaning', groupId: 'housing', name: 'Cleaning Services', isSpend: true },

  // Entertainment & Leisure
  { id: 'cinema-events', groupId: 'entertainment', name: 'Cinema & Live Events', isSpend: true },
  { id: 'sport-recreation', groupId: 'entertainment', name: 'Sport & Recreation', isSpend: true },
  { id: 'hobbies', groupId: 'entertainment', name: 'Hobbies', isSpend: true },
  { id: 'gaming', groupId: 'entertainment', name: 'Gaming', isSpend: true },
  { id: 'books-media', groupId: 'entertainment', name: 'Books, Music & Media', isSpend: true },

  // Travel
  { id: 'flights', groupId: 'travel', name: 'Flights', isSpend: true },
  { id: 'accommodation', groupId: 'travel', name: 'Accommodation', isSpend: true },
  { id: 'car-hire', groupId: 'travel', name: 'Car Hire', isSpend: true },
  { id: 'travel-insurance', groupId: 'travel', name: 'Travel Insurance', isSpend: true },
  { id: 'travel-other', groupId: 'travel', name: 'Travel (Other)', isSpend: true },

  // Education
  { id: 'school-fees', groupId: 'education', name: 'School / Childcare Fees', isSpend: true },
  { id: 'hecs', groupId: 'education', name: 'HECS / HELP Repayment', isSpend: false },
  { id: 'courses-training', groupId: 'education', name: 'Courses & Training', isSpend: true },
  { id: 'textbooks-supplies', groupId: 'education', name: 'Textbooks & Supplies', isSpend: true },

  // Financial & Insurance
  { id: 'bank-fees', groupId: 'financial', name: 'Bank Fees & Charges', isSpend: true },
  { id: 'atm-cash', groupId: 'financial', name: 'ATM / Cash Withdrawal', isSpend: true },
  { id: 'tax-paid', groupId: 'financial', name: 'Tax Paid (BAS / PAYG)', isSpend: false },
  { id: 'life-insurance', groupId: 'financial', name: 'Life / Income Protection Insurance', isSpend: true },
  { id: 'investment-fee', groupId: 'financial', name: 'Investment Platform Fees', isSpend: true },
  { id: 'accountant', groupId: 'financial', name: 'Accountant / Financial Adviser', isSpend: true },
  { id: 'interest-charges', groupId: 'financial', name: 'Interest Charges (Credit Card)', isSpend: true },

  // Savings & Investments
  { id: 'savings-transfer', groupId: 'savings-investments', name: 'Transfer to Savings', isSpend: false },
  { id: 'super-contribution', groupId: 'savings-investments', name: 'Super Contribution (Voluntary)', isSpend: false },
  { id: 'shares-etf', groupId: 'savings-investments', name: 'Shares / ETF Purchase', isSpend: false },
  { id: 'crypto', groupId: 'savings-investments', name: 'Cryptocurrency', isSpend: false },
  { id: 'term-deposit', groupId: 'savings-investments', name: 'Term Deposit', isSpend: false },
  { id: 'offset-deposit', groupId: 'savings-investments', name: 'Offset Account Deposit', isSpend: false },

  // Transfers
  { id: 'internal-transfer', groupId: 'transfers', name: 'Internal Transfer (Between Own Accounts)', isSpend: false },
  { id: 'pay-anyone', groupId: 'transfers', name: 'Pay Anyone / BPAY', isSpend: false },
  { id: 'direct-debit', groupId: 'transfers', name: 'Direct Debit', isSpend: false },

  // Uncategorised — default for needs-review items
  {
    id: 'uncategorised',
    groupId: 'uncategorised',
    name: 'Uncategorised',
    isSpend: false,
    isNeedsReviewDefault: true,
  },
]);

// ---------------------------------------------------------------------------
// Frozen taxonomy object
// ---------------------------------------------------------------------------

export const TAXONOMY = Object.freeze({
  groups: GROUPS,
  categories: CATEGORIES,
}) satisfies { groups: ReadonlyArray<CategoryGroup>; categories: ReadonlyArray<CategoryRecord> };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Look up a category by its id. Returns undefined if not found. */
export function categoryById(id: string): CategoryRecord | undefined {
  return CATEGORIES.find((c) => c.id === id);
}

/** All categories belonging to a given group. */
export function categoriesInGroup(groupId: string): ReadonlyArray<CategoryRecord> {
  return CATEGORIES.filter((c) => c.groupId === groupId);
}

/** Whether a category id represents a spending category. */
export function isSpendCategory(id: string): boolean {
  const cat = categoryById(id);
  return cat?.isSpend ?? false;
}

/** The canonical needs-review category id. */
export const NEEDS_REVIEW_CATEGORY_ID = 'uncategorised' as const;
